
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

const emptyForm = {
  title: "",
  procurement_category_id: "",
  department_id: "",
  vendor_id: "",
  quantity: "1",
  estimated_cost: "",
  request_date: new Date().toISOString().slice(0, 10),
  required_date: "",
  priority: "Medium",
  description: "",
};

const statuses = [
  "Pending Approval",
  "Approved",
  "Rejected",
  "Ordered",
  "Received",
  "Cancelled",
];

const priorities = ["Critical", "High", "Medium", "Low"];

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const dateLabel = (value) => {
  if (!value) return "—";

  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const getList = (response) => {
  const body = response?.data;
  const data = body?.data ?? body;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const getPagination = (response) => {
  const body = response?.data;
  const data = body?.data;

  if (data && !Array.isArray(data) && typeof data === "object") {
    return {
      currentPage: Number(data.current_page || 1),
      lastPage: Number(data.last_page || 1),
      total: Number(data.total ?? data.data?.length ?? 0),
    };
  }

  return {
    currentPage: Number(body?.pagination?.current_page || 1),
    lastPage: Number(body?.pagination?.last_page || 1),
    total: Number(body?.pagination?.total ?? getList(response).length),
  };
};

const getErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;

  if (errors) {
    return Object.values(errors).flat().join(" ");
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

const statusClass = (status) => {
  const classes = {
    "Pending Approval": "bg-amber-50 text-amber-700 border-amber-200",
    Approved: "bg-blue-50 text-blue-700 border-blue-200",
    Rejected: "bg-red-50 text-red-700 border-red-200",
    Ordered: "bg-violet-50 text-violet-700 border-violet-200",
    Received: "bg-green-50 text-green-700 border-green-200",
    Cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return classes[status] || classes["Pending Approval"];
};

const priorityClass = (priority) => {
  const classes = {
    Critical: "bg-red-50 text-red-700 border-red-200",
    High: "bg-amber-50 text-amber-700 border-amber-200",
    Medium: "bg-blue-50 text-blue-700 border-blue-200",
    Low: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return classes[priority] || classes.Medium;
};

const ProcurementManagement = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  const [statusModal, setStatusModal] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: "Pending Approval",
    purchase_order_number: "",
    received_quantity: "0",
  });

  const fetchLookups = useCallback(async () => {
    setLookupsLoading(true);

    try {
      const [categoryResponse, departmentResponse, vendorResponse] =
        await Promise.all([
          axiosInstance.get("/procurement-categories", {
            params: { per_page: 100 },
          }),
          axiosInstance.get("/departments", {
            params: { per_page: 100 },
          }),
          axiosInstance.get("/vendor-management", {
            params: { per_page: 100 },
          }),
        ]);

      setCategories(getList(categoryResponse));
      setDepartments(getList(departmentResponse));
      setVendors(getList(vendorResponse));
    } catch (err) {
      setError(
        `Could not load dropdown data: ${getErrorMessage(err)}`
      );
    } finally {
      setLookupsLoading(false);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        per_page: 10,
      };

      if (search.trim()) params.search = search.trim();
      if (tab !== "all") {
        params.status = {
          pending: "Pending Approval",
          approved: "Approved",
          rejected: "Rejected",
          ordered: "Ordered",
          received: "Received",
          cancelled: "Cancelled",
        }[tab];
      }

      if (priorityFilter !== "all") {
        params.priority =
          priorities.find(
            (value) => value.toLowerCase() === priorityFilter
          ) || priorityFilter;
      }

      if (categoryFilter !== "all") {
        params.procurement_category_id = categoryFilter;
      }

      if (departmentFilter !== "all") {
        params.department_id = departmentFilter;
      }

      if (vendorFilter !== "all") {
        params.vendor_id = vendorFilter;
      }

      const response = await axiosInstance.get(
        "/procurement-management",
        { params }
      );

      const list = getList(response);
      const pagination = getPagination(response);

      setRows(list);
      setLastPage(pagination.lastPage);
      setTotal(pagination.total);
    } catch (err) {
      setRows([]);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    search,
    tab,
    priorityFilter,
    categoryFilter,
    departmentFilter,
    vendorFilter,
  ]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const stats = useMemo(() => {
    const all = rows;

    return {
      pageCount: all.length,
      pending: all.filter((r) => r.status === "Pending Approval").length,
      ordered: all.filter((r) => r.status === "Ordered").length,
      received: all.filter((r) => r.status === "Received").length,
      value: all.reduce(
        (sum, r) => sum + Number(r.estimated_cost || 0),
        0
      ),
    };
  }, [rows]);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormError("");
  };

  const openCreate = () => {
    setEditing(null);
    setViewing(null);
    setForm({
      ...emptyForm,
      request_date: new Date().toISOString().slice(0, 10),
    });
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setViewing(null);

    setForm({
      title: row.title || "",
      procurement_category_id: String(row.procurement_category_id || ""),
      department_id: String(row.department_id || ""),
      vendor_id: String(row.vendor_id || ""),
      quantity: String(row.quantity ?? 1),
      estimated_cost: String(row.estimated_cost ?? ""),
      request_date: String(row.request_date || "").slice(0, 10),
      required_date: String(row.required_date || "").slice(0, 10),
      priority: row.priority || "Medium",
      description: row.description || "",
    });

    setFormError("");
    setModalOpen(true);
  };

  const saveRequest = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");

    const payload = {
      ...form,
      procurement_category_id: Number(form.procurement_category_id),
      department_id: Number(form.department_id),
      vendor_id: Number(form.vendor_id),
      quantity: Number(form.quantity),
      estimated_cost: Number(form.estimated_cost),
    };

    try {
      if (editing) {
        await axiosInstance.put(
          `/procurement-management/${editing.id}`,
          payload
        );
        setNotice("Procurement request updated successfully.");
      } else {
        await axiosInstance.post("/procurement-management", payload);
        setNotice("Procurement request created successfully.");
      }

      setModalOpen(false);
      setEditing(null);
      setPage(1);
      await fetchRequests();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openStatusEditor = (row) => {
    setStatusModal(row);
    setStatusForm({
      status: row.status || "Pending Approval",
      purchase_order_number: row.purchase_order_number || "",
      received_quantity: String(row.received_quantity ?? 0),
    });
  };

  const saveStatus = async (event) => {
    event.preventDefault();
    if (!statusModal) return;

    setSaving(true);
    setFormError("");

    try {
      await axiosInstance.patch(
        `/procurement-management/${statusModal.id}/status`,
        {
          ...statusForm,
          received_quantity: Number(statusForm.received_quantity),
        }
      );

      setStatusModal(null);
      setNotice("Procurement status updated successfully.");
      await fetchRequests();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteRequest = async (row) => {
    if (!window.confirm(`Delete request "${row.request_number}"?`)) return;

    setError("");
    setNotice("");

    try {
      await axiosInstance.delete(`/procurement-management/${row.id}`);
      setNotice("Procurement request deleted successfully.");
      await fetchRequests();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const exportCsv = () => {
    const columns = [
      "request_number",
      "title",
      "category",
      "department",
      "vendor",
      "quantity",
      "received_quantity",
      "estimated_cost",
      "request_date",
      "required_date",
      "priority",
      "status",
      "purchase_order_number",
    ];

    const csvCell = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const data = rows.map((row) => ({
      request_number: row.request_number,
      title: row.title,
      category: row.category?.name || "",
      department: row.department?.name || "",
      vendor: row.vendor?.name || "",
      quantity: row.quantity,
      received_quantity: row.received_quantity,
      estimated_cost: row.estimated_cost,
      request_date: row.request_date,
      required_date: row.required_date,
      priority: row.priority,
      status: row.status,
      purchase_order_number: row.purchase_order_number,
    }));

    const csv = [
      columns.join(","),
      ...data.map((row) => columns.map((key) => csvCell(row[key])).join(",")),
    ].join("\n");

    const url = URL.createObjectURL(
      new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
    );

    const link = document.createElement("a");
    link.href = url;
    link.download = "procurement-requests.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    ["all", "All Requests"],
    ["pending", "Pending Approval"],
    ["approved", "Approved"],
    ["rejected", "Rejected"],
    ["ordered", "Ordered"],
    ["received", "Received"],
    ["cancelled", "Cancelled"],
  ];

  const inputClass =
    "w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/10";

  const labelClass = "mb-1.5 block text-sm font-medium text-foreground";

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-layout ${
            collapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="space-y-6 p-6 lg:p-8">
            <Breadcrumb />

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon name="ShoppingCart" size={24} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
                    Procurement Management
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Manage procurement requests, suppliers, approvals, and delivery.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={exportCsv}
                >
                  Export CSV
                </Button>

                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={openCreate}
                >
                  New Request
                </Button>
              </div>
            </div>

            {error && (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => setError("")}
                  aria-label="Dismiss error"
                >
                  <Icon name="X" size={17} />
                </button>
              </div>
            )}

            {notice && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                <span>{notice}</span>
                <button type="button" onClick={() => setNotice("")}>
                  <Icon name="X" size={17} />
                </button>
              </div>
            )}

            {lookupsLoading && (
              <div className="rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
                Loading procurement categories, departments, and vendors…
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {[
                ["Requests on Page", stats.pageCount, "ClipboardList"],
                ["Pending Approval", stats.pending, "Clock3"],
                ["Ordered", stats.ordered, "Truck"],
                ["Received", stats.received, "PackageCheck"],
                ["Value on Page", money(stats.value), "IndianRupee"],
              ].map(([label, value, icon]) => (
                <div
                  key={label}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">{label}</p>
                      <p className="mt-2 break-words text-xl font-bold text-foreground">
                        {value}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon name={icon} size={20} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <section className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="space-y-4 border-b border-border p-5">
                <div>
                  <h2 className="font-semibold text-foreground">
                    Procurement Requests
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Records are loaded from your Laravel API.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  <input
                    className={inputClass}
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="Search requests..."
                  />

                  <select
                    className={inputClass}
                    value={categoryFilter}
                    onChange={(event) => {
                      setCategoryFilter(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Categories</option>
                    {categories.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={inputClass}
                    value={departmentFilter}
                    onChange={(event) => {
                      setDepartmentFilter(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Departments</option>
                    {departments.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={inputClass}
                    value={vendorFilter}
                    onChange={(event) => {
                      setVendorFilter(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Vendors</option>
                    {vendors.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={inputClass}
                    value={priorityFilter}
                    onChange={(event) => {
                      setPriorityFilter(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">All Priorities</option>
                    {priorities.map((item) => (
                      <option key={item} value={item.toLowerCase()}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap gap-2">
                  {tabs.map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        setTab(id);
                        setPage(1);
                      }}
                      className={`rounded-lg px-3 py-2 text-sm font-medium ${
                        tab === id
                          ? "bg-primary text-white"
                          : "border border-border bg-background text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      {[
                        "Request",
                        "Category",
                        "Department",
                        "Vendor",
                        "Quantity",
                        "Required Date",
                        "Estimated Cost",
                        "Priority",
                        "Status",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="whitespace-nowrap px-4 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-4 py-14 text-center text-sm text-muted-foreground"
                        >
                          Loading procurement requests…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-16 text-center">
                          <Icon
                            name="ClipboardList"
                            size={28}
                            className="mx-auto text-muted-foreground"
                          />
                          <p className="mt-3 font-semibold text-foreground">
                            No procurement requests found
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            Create a request or change your filters.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="px-4 py-4">
                            <p className="font-medium text-foreground">
                              {row.title}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {row.request_number}
                            </p>
                            {row.purchase_order_number && (
                              <p className="mt-1 text-xs text-primary">
                                PO: {row.purchase_order_number}
                              </p>
                            )}
                          </td>

                          <td className="px-4 py-4 text-sm">
                            {row.category?.name || "—"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {row.department?.name || "—"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {row.vendor?.name || "—"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <span>
                              {row.received_quantity || 0} / {row.quantity}
                            </span>
                            <div className="mt-2 h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{
                                  width: `${
                                    row.quantity
                                      ? Math.min(
                                          100,
                                          (Number(row.received_quantity || 0) /
                                            Number(row.quantity)) *
                                            100
                                        )
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm">
                            {dateLabel(row.required_date)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm font-medium">
                            {money(row.estimated_cost)}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`rounded-md border px-2 py-1 text-xs font-medium ${priorityClass(row.priority)}`}
                            >
                              {row.priority}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass(row.status)}`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                title="View request"
                                onClick={() => setViewing(row)}
                                className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                              >
                                <Icon name="Eye" size={17} />
                              </button>
                              <button
                                type="button"
                                title="Edit request"
                                onClick={() => openEdit(row)}
                                className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                              >
                                <Icon name="Pencil" size={17} />
                              </button>
                              <button
                                type="button"
                                title="Update status"
                                onClick={() => openStatusEditor(row)}
                                className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                              >
                                <Icon name="RefreshCw" size={17} />
                              </button>
                              <button
                                type="button"
                                title="Delete request"
                                onClick={() => deleteRequest(row)}
                                className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                              >
                                <Icon name="Trash2" size={17} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
                <p className="text-sm text-muted-foreground">
                  {total} total request(s) · Page {page} of {lastPage}
                </p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                    className="rounded-lg border border-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= lastPage || loading}
                    onClick={() =>
                      setPage((value) => Math.min(lastPage, value + 1))
                    }
                    className="rounded-lg border border-border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <button
            type="button"
            aria-label="Close request form"
            onClick={() => setModalOpen(false)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative flex h-full w-full max-w-2xl flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {editing ? "Edit Procurement Request" : "New Procurement Request"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Select the category, department, and supplier from your existing modules.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={saveRequest} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-5 overflow-y-auto p-6">
                {formError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Request Title *</label>
                  <input
                    required
                    maxLength={255}
                    className={inputClass}
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Enter procurement requirement"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>Procurement Category *</label>
                    <select
                      required
                      className={inputClass}
                      value={form.procurement_category_id}
                      onChange={(e) =>
                        setField("procurement_category_id", e.target.value)
                      }
                    >
                      <option value="">Select category</option>
                      {categories.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={item.is_active === false || item.is_active === 0}
                        >
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Department *</label>
                    <select
                      required
                      className={inputClass}
                      value={form.department_id}
                      onChange={(e) => setField("department_id", e.target.value)}
                    >
                      <option value="">Select department</option>
                      {departments.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={String(item.status).toLowerCase() === "inactive"}
                        >
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vendor / Supplier *</label>
                    <select
                      required
                      className={inputClass}
                      value={form.vendor_id}
                      onChange={(e) => setField("vendor_id", e.target.value)}
                    >
                      <option value="">Select vendor</option>
                      {vendors.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                          disabled={String(item.status).toLowerCase() === "inactive"}
                        >
                          {item.name} {item.code ? `(${item.code})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Priority *</label>
                    <select
                      required
                      className={inputClass}
                      value={form.priority}
                      onChange={(e) => setField("priority", e.target.value)}
                    >
                      {priorities.map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Quantity *</label>
                    <input
                      required
                      type="number"
                      min="1"
                      step="1"
                      className={inputClass}
                      value={form.quantity}
                      onChange={(e) => setField("quantity", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Estimated Cost (INR) *</label>
                    <input
                      required
                      type="number"
                      min="0"
                      step="0.01"
                      className={inputClass}
                      value={form.estimated_cost}
                      onChange={(e) =>
                        setField("estimated_cost", e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Request Date *</label>
                    <input
                      required
                      type="date"
                      className={inputClass}
                      value={form.request_date}
                      onChange={(e) => setField("request_date", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Required Date *</label>
                    <input
                      required
                      type="date"
                      min={form.request_date}
                      className={inputClass}
                      value={form.required_date}
                      onChange={(e) => setField("required_date", e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Description / Justification</label>
                  <textarea
                    rows={4}
                    maxLength={5000}
                    className={inputClass}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Describe the business requirement and specifications."
                  />
                </div>

                {editing && (
                  <p className="text-sm text-muted-foreground">
                    Request number and approval status are managed separately.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t border-border bg-card p-5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || lookupsLoading}>
                  {saving
                    ? "Saving..."
                    : editing
                      ? "Save Changes"
                      : "Create Request"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {statusModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close status editor"
            onClick={() => setStatusModal(null)}
            className="absolute inset-0 bg-black/50"
          />

          <form
            onSubmit={saveStatus}
            className="relative w-full max-w-lg space-y-5 rounded-xl border border-border bg-background p-6 shadow-2xl"
          >
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Update Procurement Status
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {statusModal.request_number} — {statusModal.title}
              </p>
            </div>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div>
              <label className={labelClass}>Status *</label>
              <select
                required
                className={inputClass}
                value={statusForm.status}
                onChange={(e) =>
                  setStatusForm((value) => ({
                    ...value,
                    status: e.target.value,
                  }))
                }
              >
                {statuses.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Purchase Order Number</label>
              <input
                className={inputClass}
                maxLength={100}
                value={statusForm.purchase_order_number}
                onChange={(e) =>
                  setStatusForm((value) => ({
                    ...value,
                    purchase_order_number: e.target.value,
                  }))
                }
                placeholder="Enter PO number if applicable"
              />
            </div>

            <div>
              <label className={labelClass}>Received Quantity *</label>
              <input
                required
                type="number"
                min="0"
                max={statusModal.quantity}
                className={inputClass}
                value={statusForm.received_quantity}
                onChange={(e) =>
                  setStatusForm((value) => ({
                    ...value,
                    received_quantity: e.target.value,
                  }))
                }
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Requested quantity: {statusModal.quantity}
              </p>
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStatusModal(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Update Status"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close request details"
            onClick={() => setViewing(null)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {viewing.request_number}
                </p>
                <h2 className="mt-1 text-xl font-semibold text-foreground">
                  {viewing.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                ["Category", viewing.category?.name],
                ["Department", viewing.department?.name],
                ["Vendor", viewing.vendor?.name],
                ["Quantity", `${viewing.received_quantity || 0} / ${viewing.quantity}`],
                ["Estimated Cost", money(viewing.estimated_cost)],
                ["Request Date", dateLabel(viewing.request_date)],
                ["Required Date", dateLabel(viewing.required_date)],
                ["Priority", viewing.priority],
                ["Status", viewing.status],
                ["Purchase Order", viewing.purchase_order_number || "—"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-border p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 break-words text-sm font-medium text-foreground">
                    {value || "—"}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">Description</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {viewing.description || "No description provided."}
              </p>
            </div>

            <div className="mt-5 flex justify-end">
              <Button variant="outline" onClick={() => setViewing(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcurementManagement;
